import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, AlertTriangle, Info, CircleAlert, ArrowRight, Sparkles, RotateCcw, BookOpen, Keyboard } from "lucide-react";
import { helpChapterFor } from "@/lib/helpMap";
import { pageMetaFor } from "@/lib/pageMeta";
import { useDraggableLauncher } from "@/lib/useDraggableLauncher";

const SEV = {
  danger: { cls: "border-rose-500/40 bg-rose-500/[0.07]", icon: CircleAlert, ic: "text-rose-400" },
  warning: { cls: "border-amber-500/40 bg-amber-500/[0.07]", icon: AlertTriangle, ic: "text-amber-400" },
  info: { cls: "border-sky-500/40 bg-sky-500/[0.06]", icon: Info, ic: "text-sky-400" },
};

const SUGGESTIONS = [
  "How do I mark a Catawiki order as shipped?",
  "How is my Catawiki sell-through doing?",
  "Which categories sell best?",
  "How do I send an offer to a customer?",
];

const newSession = () => {
  try {
    const existing = localStorage.getItem("advisor-session");
    if (existing) return existing;
  } catch { /* Chat still works without browser storage. */ }
  const s = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
  try { localStorage.setItem("advisor-session", s); } catch { /* Optional persistence. */ }
  return s;
};

export const AdvisorPanel = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const page = pageMetaFor(pathname);
  const [open, setOpen] = useState(false);
  const launcher = useDraggableLauncher(() => setOpen(true));
  const [tab, setTab] = useState("guide");
  const [attention, setAttention] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);
  if (!sessionRef.current) sessionRef.current = newSession();

  const loadAttention = useCallback(() => {
    api.get("/advisor/attention", { loadingIndicator: false }).then((r) => setAttention(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadAttention();
    const t = setInterval(loadAttention, 5 * 60 * 1000);
    const openChat = () => { setOpen(true); setTab("chat"); };
    window.addEventListener("open-advisor", openChat);
    return () => { clearInterval(t); window.removeEventListener("open-advisor", openChat); };
  }, [loadAttention]);

  useEffect(() => {
    if (!open) return;
    loadAttention();
    api.get(`/advisor/chat/${sessionRef.current}`).then((r) => setMessages(r.data.messages || [])).catch(() => {});
  }, [open, loadAttention]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [messages, tab, open]);

  const send = async (preset) => {
    const text = (preset || input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/advisor/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = p.slice(6);
          if (d === "[DONE]") continue;
          try {
            const j = JSON.parse(d);
            if (j.t) setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { ...c[c.length - 1], content: (c[c.length - 1].content || "") + j.t };
              return c;
            });
            if (j.error) setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: `⚠ ${j.error}` }]);
          } catch { /* partial frame */ }
        }
      }
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: "⚠ Connection failed — please try again." }]);
    } finally {
      setStreaming(false);
    }
  };

  const newChat = () => {
    if (streaming) return;
    try { localStorage.removeItem("advisor-session"); } catch { /* Optional persistence. */ }
    sessionRef.current = newSession();
    setMessages([]);
  };

  const goFix = (link) => { setOpen(false); navigate(link); };
  const issues = attention.length;

  return (
    <>
      <button
        data-testid="advisor-fab"
        type="button"
        {...launcher}
        aria-label="Milora Assistant"
        aria-describedby="advisor-move-help"
        title="Click to open · Drag to move · Alt + arrows to move · Alt + Home to reset"
        className="group fixed z-50 w-[52px] h-[52px] p-3.5 cursor-grab active:cursor-grabbing rounded-full bg-[#7DD3FC] text-[#0A0D14] shadow-lg shadow-[#7DD3FC]/20 hover:bg-[#9FE0FF] transition-colors"
      >
        <Bot className="w-6 h-6" />
        {issues > 0 && (
          <span data-testid="advisor-fab-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">
            {issues}
          </span>
        )}
        <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-2 z-[90] whitespace-nowrap rounded-lg border border-[#263044] bg-[#111520] px-2.5 py-1.5 text-[11px] font-medium text-slate-200 opacity-0 shadow-xl transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100">Milora Assistant</span>
      </button>
      <span id="advisor-move-help" className="sr-only">Drag to move the Assistant button. With keyboard focus, use Alt and arrow keys to move it, or Alt and Home to reset its position.</span>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" data-testid="advisor-panel" className="bg-[#111520] border-[#1E293B] text-slate-100 w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#1E293B]">
            <SheetTitle className="font-display text-2xl text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#7DD3FC]" /> Advisor
            </SheetTitle>
            <SheetDescription className="text-slate-400 text-xs">
              Live checks on your data — plus an AI you can ask about performance and priorities.
            </SheetDescription>
            <div className="flex gap-2 mt-2">
              <button data-testid="advisor-tab-guide" onClick={() => setTab("guide")}
                className={`h-8 px-3 rounded-md text-xs font-mono-data border transition-colors ${tab === "guide" ? "bg-[#7DD3FC]/15 text-[#7DD3FC] border-[#7DD3FC]/40" : "bg-[#0A0D14] text-slate-400 border-[#1E293B] hover:text-slate-200"}`}>
                Guide
              </button>
              <button data-testid="advisor-tab-attention" onClick={() => setTab("attention")}
                className={`h-8 px-3 rounded-md text-xs font-mono-data border transition-colors ${tab === "attention" ? "bg-[#7DD3FC]/15 text-[#7DD3FC] border-[#7DD3FC]/40" : "bg-[#0A0D14] text-slate-400 border-[#1E293B] hover:text-slate-200"}`}>
                Needs attention{issues > 0 ? ` · ${issues}` : ""}
              </button>
              <button data-testid="advisor-tab-chat" onClick={() => setTab("chat")}
                className={`h-8 px-3 rounded-md text-xs font-mono-data border transition-colors ${tab === "chat" ? "bg-[#7DD3FC]/15 text-[#7DD3FC] border-[#7DD3FC]/40" : "bg-[#0A0D14] text-slate-400 border-[#1E293B] hover:text-slate-200"}`}>
                Ask AI
              </button>
              {tab === "chat" && (
                <button data-testid="advisor-new-chat" onClick={newChat} disabled={streaming || messages.length === 0}
                  title="Start a fresh conversation"
                  className="group relative h-8 w-8 p-0 ml-auto rounded-md border bg-[#0A0D14] text-slate-400 border-[#1E293B] hover:text-[#7DD3FC] hover:border-[#7DD3FC]/40 transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="pointer-events-none absolute top-full right-0 mt-2 z-[90] whitespace-nowrap rounded-lg border border-[#263044] bg-[#111520] px-2.5 py-1.5 text-[11px] font-medium text-slate-200 opacity-0 shadow-xl transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100">Reset conversation</span>
                </button>
              )}
            </div>
          </SheetHeader>

          {tab === "guide" ? (
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="advisor-page-guide">
              <div className="rounded-xl border border-[#263044] bg-[#0A0D14] p-4">
                <div className="flex items-center gap-2 text-[#7DD3FC]"><BookOpen className="w-4" /><p className="text-sm font-semibold">Help for {page.title}</p></div>
                <p className="text-xs text-slate-400 leading-5 mt-3">Ask the Assistant about anything on this page, or open its complete step-by-step guide.</p>
                <button onClick={() => { setOpen(false); navigate(`/help?ch=${helpChapterFor(pathname)}`); }} className="mt-4 text-xs text-[#7DD3FC] hover:text-[#9FE0FF] transition-colors flex items-center gap-2">Open the full page guide <ArrowRight className="w-3.5" /></button>
              </div>
              <button onClick={() => setTab("chat")} className="w-full rounded-xl border border-[#263044] bg-[#111520] p-4 text-left hover:border-[#7DD3FC]/40 transition-colors"><p className="text-xs font-semibold text-slate-200 flex items-center gap-2"><Sparkles className="w-4 text-[#7DD3FC]" />Ask about this page</p><p className="text-[11px] text-slate-500 mt-2">The AI can explain workflows, fields, statuses and next steps.</p></button>
              <button onClick={() => { setTab("chat"); send("Show me the keyboard shortcuts for this ERP"); }} className="w-full rounded-xl border border-[#263044] bg-[#111520] p-4 text-left hover:border-[#7DD3FC]/40 transition-colors"><p className="text-xs font-semibold text-slate-200 flex items-center gap-2"><Keyboard className="w-4 text-[#7DD3FC]" />Keyboard shortcuts</p><p className="text-[11px] text-slate-500 mt-2">Open a chat with the available navigation shortcuts.</p></button>
            </div>
          ) : tab === "attention" ? (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="advisor-attention-list">
              {attention.length === 0 && (
                <div className="text-center py-16">
                  <Sparkles className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-300">All clear — nothing needs your attention right now.</p>
                </div>
              )}
              {attention.map((it) => {
                const S = SEV[it.severity] || SEV.info;
                const Icon = S.icon;
                return (
                  <button key={it.key} data-testid={`advisor-item-${it.key}`} onClick={() => goFix(it.link)}
                    className={`w-full text-left rounded-lg border p-3 transition-colors hover:brightness-110 ${S.cls}`}>
                    <div className="flex items-start gap-2.5">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${S.ic}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-100 font-medium">{it.label} <span className={`font-mono-data ${S.ic}`}>({it.count})</span></p>
                        <p className="text-xs text-slate-400 mt-0.5">{it.hint}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="advisor-chat-messages">
                {messages.length === 0 && (
                  <div className="pt-8 space-y-2">
                    <p className="text-xs text-slate-500 text-center mb-3">Ask anything about your stock, sales or Catawiki performance:</p>
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid="advisor-suggestion"
                        className="w-full text-left text-sm rounded-lg border border-[#1E293B] bg-[#0A0D14] px-3 py-2.5 text-slate-300 hover:border-[#7DD3FC] hover:text-[#7DD3FC] transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`max-w-[88%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "ml-auto bg-[#7DD3FC]/15 text-slate-100 border border-[#7DD3FC]/30" : "bg-[#0A0D14] border border-[#1E293B] text-slate-200"}`}>
                    {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-[#1E293B] flex gap-2">
                <Input data-testid="advisor-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask the advisor…" disabled={streaming}
                  className="bg-[#0A0D14] border-[#1E293B]" />
                <Button data-testid="advisor-chat-send" onClick={() => send()} disabled={streaming || !input.trim()}
                  className="bg-[#7DD3FC] text-[#0A0D14] hover:bg-[#38BDF8] px-3">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
